import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { Call, Device, type TwilioError } from '@twilio/voice-sdk'
import { nanoid } from 'nanoid'
import { type AxiosResponse } from 'axios'
import { LINK_ENABLE_MICROPHONE } from 'shared/constants/links'
import { toastStore } from 'shared/ui'
import { NODE_ENV } from 'shared/config'
import { logger, writeTextToClipboard } from 'shared/lib'
import { CheckInternet } from 'shared/store/CheckInternet'
import { RequestCancelAxios } from 'shared/api/lib'
import { sentEventSentry } from 'entities/Sentry/lib/sentEventSentry'
import {
  type ICallStoreSignal,
  type IConnectTwilioExtraParams,
  type IResponseTwilioContact,
  type IResponseTwilioConversation,
  type IResponseTwilioInbox,
} from 'entities/Call'
import { CallApi } from 'entities/Call/api/call'
import {
  Conversation,
  ConversationsApi,
  type ConversationsStore,
  type IResponseConversation,
} from 'entities/Conversation'
import { UsersApi } from 'entities/Users/api/users'
import { DeviceItemsEnum, DeviceSettingsStore } from 'entities/DeviceSettings'
import { Contact } from 'entities/Contacts/model/Contact'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import { ActiveCallingStore } from 'entities/Call/store/activeCallingStore'
import { type UsersStore } from 'entities/Users'
import { type InboxesStore } from 'entities/Inbox'
import { type ContactsStore } from 'entities/Contacts'
import type { Websocket } from 'entities/WebSocket'
import { type IParamsSendFromSmartOption } from 'entities/SendFrom'
import { type OrganizationStore } from 'entities/Organization'
import { SharedWorkerCallStore } from 'entities/Call/store/sharedWorkerCallStore'

interface TwilioSound {
  _audio?: HTMLAudioElement
  _src?: string
  url?: string
}

type HTMLAudioElementWithSinkId = HTMLAudioElement & {
  setSinkId?: (deviceId: string) => Promise<void>
}

export class CallStore {
  private activeCallingStore
  private _device: Device | null = null
  private _connect: Call | null = null
  private _token: string | null = null
  private _loading = true
  private _disabled = true
  private _contactTo: Contact | null = null
  private _contactFrom: Inbox | null = null
  private _status: Call.State = Call.State.Closed
  private _started = false
  private _direction: Call.CallDirection | null = null
  private _conversationId: number | null = null
  private _inboxId: number | null = null

  private _isRecord = false
  private _isRecordLoading = false
  private _isRingEverywhere = false
  private _isRingEverywhereLoading = false
  private _ringEverywhereAudioElements: HTMLAudioElement[] = []
  private _ringEverywhereStopped = false

  private _streams: MediaStream[] = []
  private _signal: ICallStoreSignal | null = null

  private _isInvalidToken = false
  private _voicemailDropCompleted = false

  private _deviceSettingsStore = new DeviceSettingsStore({
    items: [
      DeviceItemsEnum.audioOutput,
      DeviceItemsEnum.audioInput,
      DeviceItemsEnum.recordAuto,
      DeviceItemsEnum.defaultInbox,
    ],
  })
  private _disposeStatus: IReactionDisposer | null = null
  private _disposeSelectedAudioInput: IReactionDisposer | null = null
  private _disposeSelectedAudioOutput: IReactionDisposer | null = null
  private _disposeUserSettings: IReactionDisposer | null = null
  private _worker = new Worker('/worker.js')
  private _sharedWorkerCallStore: SharedWorkerCallStore

  private _incomingCallCancelRequest = new RequestCancelAxios()

  constructor(
    private _usersStore: UsersStore,
    private _inboxesStore: InboxesStore,
    private _conversationStore: ConversationsStore,
    private _contactsStore: ContactsStore,
    private _websocket: Websocket,
    private _organizationStore: OrganizationStore
  ) {
    makeAutoObservable(this)
    this.activeCallingStore = new ActiveCallingStore(this, this._usersStore, this._websocket)
    this._sharedWorkerCallStore = new SharedWorkerCallStore(this)

    this._worker.onmessage = (e) => {
      if (e.data.action === 'detect_fast') {
        if (this._isInvalidToken) return
        if (CheckInternet.isOnline) {
          if (this._device?.state === 'unregistered') {
            this.destroyDevice()
            this.initDevice()
          }
        }
      }
    }

    this.reactionStatus()
    this.reactionUserSettings()
  }

  get deviceSettingsStore() {
    return this._deviceSettingsStore
  }

  get hasActiveCall() {
    return this.activeCallingStore.hasActiveCall
  }

  reset = () => {
    this._direction = null
    this._status = Call.State.Closed
    this._inboxId = null
    this._conversationId = null
    this._contactFrom = null
    this._contactTo = null
    this._signal = null
    this.stopStreams()
    this.deviceSettingsStore.removeStream()
  }

  setVoicemailDropCompleted = (value: boolean) => {
    this._voicemailDropCompleted = value
  }

  setCallStatus = (status: Call.State) => {
    this._status = status
  }

  initDevice = async () => {
    try {
      const checkMicrophone = await this.checkMicrophone()

      sentEventSentry({
        event: '[WEB] Check Microphone',
        name: 'calling',
        level: 'log',
        tags: {
          enableMicrophone: checkMicrophone,
        },
        sampleRate: 0.2,
      })

      if (!checkMicrophone) return

      runInAction(() => {
        this._disabled = false
      })

      const response = await CallApi.createVoiceToken()

      sentEventSentry({
        event: '[Twilio:Calling] token_created',
        name: 'calling',
        level: 'log',
        sampleRate: 0.2,
      })

      await this._usersStore.initPromise

      runInAction(async () => {
        this._token = response.data.token
        this._isRecord = this._usersStore.isRecordAutomatically
        this._isRingEverywhere = this._usersStore.isRingEverywhere

        this.destroyDevice()

        // https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice#deviceoptions
        this._device = new Device(this._token, {
          tokenRefreshMs: 60000 * 30,
          logLevel: 3,
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        })

        this._device.audio?.incoming(false)

        this.eventsDevice()

        await this._device.register().catch((e) => {
          logger.error(e)
        })
      })
    } catch (e) {
      logger.error(e)

      sentEventSentry({
        event: '[Twilio:Calling] initDevice',
        error: e,
        name: 'calling',
        level: 'error',
        sampleRate: 0.2,
      })
    }
  }

  destroyDevice = () => {
    this._device?.destroy()
  }

  initDevices = async () => {
    if (!this._device?.audio) return

    const outputDevices = Array.from(this._device.audio.availableOutputDevices.values())

    if (outputDevices.length) {
      await this.setOutputDevice(outputDevices[0].deviceId)
    }
  }

  checkMicrophone = (): Promise<boolean> => {
    return new Promise(async (resolve) => {
      try {
        const stream = await window.navigator.mediaDevices.getUserMedia({ audio: true })

        this.stopStreamInputTrack(stream)
        this.addStream(stream)

        resolve(true)
      } catch (e) {
        resolve(false)
      }
    })
  }

  setInputDevice = async (id: string) => {
    if (!this._device?.audio) return

    try {
      await this._device.audio.unsetInputDevice()
      await this._device.audio.setInputDevice(id)
    } catch (e) {
      logger.error(e)
    }
  }

  setOutputDevice = async (id: string) => {
    if (!this._device?.audio) return

    try {
      await this._device.audio.speakerDevices.set(id)
      await this._device.audio.ringtoneDevices.set(id)
    } catch (e) {
      logger.error(e)
    }
  }

  updateConnect = (connect?: Call) => {
    this._connect = connect ? connect : this._connect

    if (this._connect) {
      this._status = this._connect.status()
      this._direction = this._connect.direction
    }
  }

  hideCall = async () => {
    this.stopRingEverywhere()

    this.reset()
    await this._device?.audio?.unsetInputDevice()
  }

  // events device
  eventsDevice = () => {
    if (this._device) {
      this._device.on('error', this.eventDeviceError)
      this._device.on('incoming', this.eventDeviceIncoming)
      this._device.on('registered', this.eventDeviceRegistered)
      this._device.on('registering', this.eventDeviceRegistering)
      this._device.on('unregistered', this.eventDeviceUnregistered)
      this._device.on('tokenWillExpire', this.eventDeviceTokenWillExpire)
      this._device.on('destroyed', this.eventDeviceDestroyed)
      this._device?.audio?.on('deviceChange', this.eventDeviceAudioDeviceChange)
    }
  }

  eventDeviceRegistering = () => {
    sentEventSentry({
      event: '[Twilio:Calling] Device Registering',
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })
  }

  eventDeviceDestroyed = () => {
    sentEventSentry({
      event: '[Twilio:Calling] Device Destroyed',
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })
  }

  // https://www.twilio.com/docs/api/errors/20101
  // https://www.twilio.com/docs/api/errors/20104
  // https://www.twilio.com/docs/api/errors/53001
  // https://www.twilio.com/docs/voice/sdks/error-codes
  eventDeviceError = (error: TwilioError.TwilioError) => {
    this.updateConnect()
    this.log('[Twilio] - eventDeviceError')

    const twilioError = error.originalError?.twilioError

    // Invalid access token
    if (error.code === 20101) {
      this._isInvalidToken = true
    }

    if (error.code === 31000) {
      if (twilioError?.code === 53001) CheckInternet.check()
    }

    if (error.code === 53001) CheckInternet.check()
    if (error.code === 31009) CheckInternet.check()
    if (error.code === 31005) CheckInternet.check()
    if (error.code === 53000) CheckInternet.check()

    const level1 = twilioError ? ' Level 1' : ''

    sentEventSentry({
      event: `[Twilio:Calling] Device Error${level1}`,
      error: error,
      name: 'calling',
      level: 'error',
      sampleRate: 0.2,
    })

    if (twilioError) {
      sentEventSentry({
        event: '[Twilio:Calling] Device Error Level 2',
        error: twilioError,
        name: 'calling',
        level: 'error',
        sampleRate: 0.2,
      })
    }
  }

  eventDeviceIncoming = async (connect: Call) => {
    this.updateConnect(connect)
    this.eventsConnect()

    try {
      const twilioConversation: IResponseTwilioConversation = JSON.parse(
        connect.customParameters.get('conversation') || ''
      )

      const twilioContact: IResponseTwilioContact = JSON.parse(
        connect.customParameters.get('contact') || ''
      )

      const twilioInbox: IResponseTwilioInbox = JSON.parse(
        connect.customParameters.get('inbox') || ''
      )

      let conversation: IResponseConversation | null

      this._incomingCallCancelRequest.init()

      const [conversationResponse, contactFrom] = await Promise.all([
        twilioConversation.id
          ? ConversationsApi.getById(twilioConversation.id, {
              cancelToken: this._incomingCallCancelRequest.token,
            })
          : undefined,
        this._inboxesStore.getById(twilioInbox.id, true, false, {
          cancelToken: this._incomingCallCancelRequest.token,
        }),
      ])

      conversation = conversationResponse?.data || null

      if (!conversation) {
        const { data } = await ConversationsApi.getConversationsSearch(
          {
            page: 1,
            per_page: 1,
            contacts: [twilioContact.id],
            teams: [twilioInbox.id],
          },
          {
            cancelToken: this._incomingCallCancelRequest.token,
          }
        )
        conversation = data.results[0]
      }

      if (!conversation) {
        this.handleMuteIncomingNotification(true)
        sentEventSentry({
          event: '[Twilio:Calling] Incoming: not found conversation',
          name: 'calling',
          level: 'error',
          sampleRate: 0.2,
        })
        return
      }

      const contactTo = conversation.contact && new Contact(conversation.contact)
      if (!contactTo) {
        this.handleMuteIncomingNotification(true)
        sentEventSentry({
          event: '[Twilio:Calling] Incoming: not found contact',
          name: 'calling',
          level: 'error',
          sampleRate: 0.2,
        })
        return
      }

      if (contactFrom?.type !== 'inbox') {
        this.handleMuteIncomingNotification(true)
        sentEventSentry({
          event: '[Twilio:Calling] Inbound: not found inbox',
          name: 'calling',
          level: 'error',
          sampleRate: 0.2,
        })
        return
      }

      const isAway = this._usersStore.isAwayStatus
      const shouldMute = contactFrom.isMuted || isAway || this.hasActiveCall

      runInAction(() => {
        this._contactTo = contactTo
        this._contactFrom = contactFrom
        this._inboxId = conversation.inbox_id
        this._conversationId = conversation.id
        this._signal = {
          key: nanoid(),
          name: 'incoming',
        }
      })

      if (this._sharedWorkerCallStore.isWorkerActive) {
        // Tabs that are muted/away/busy don't claim ringing — the worker will
        // route the call to a free candidate from the same identity.
        if (!shouldMute) {
          this._sharedWorkerCallStore.notifyIncomingCall(connect.parameters.CallSid)
        }
      } else {
        // Fallback for browsers without SharedWorker — preserve old behavior.
        this.handleMuteIncomingNotification(shouldMute)
      }

      this.log('[Twilio] - eventDeviceIncoming')
    } catch (e) {
      logger.error(e)
    }
  }

  eventDeviceRegistered = async () => {
    this.updateConnect()
    this.log('[Twilio] - eventDeviceRegistered')

    sentEventSentry({
      event: '[Twilio:Calling] Device Registered',
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })

    await this.initDevices()

    runInAction(() => {
      this._loading = false
    })
  }

  eventDeviceUnregistered = () => {
    this.updateConnect()
    this.log('[Twilio] - eventDeviceUnregistered')

    sentEventSentry({
      event: '[Twilio:Calling] Device Unregistered',
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })
  }

  eventDeviceTokenWillExpire = async () => {
    this.updateConnect()
    this.log('[Twilio] - eventDeviceTokenWillExpire')

    sentEventSentry({
      event: '[Twilio:Calling] Device TokenWillExpire',
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })

    try {
      const response = await CallApi.createVoiceToken()

      this._device?.updateToken(response.data.token)
    } catch (e) {
      logger.error(e)

      sentEventSentry({
        event: '[Twilio:Calling] Device TokenWillExpire',
        error: e,
        name: 'calling',
        level: 'error',
        sampleRate: 0.2,
      })
    }
  }

  eventDeviceAudioDeviceChange = () => {
    this.updateConnect()
    this.log('[Twilio] - eventDeviceAudioDeviceChange')
  }

  // events connect
  eventsConnect = () => {
    if (!this._connect) return

    this._connect.on('cancel', this.eventConnectCancel)
    this._connect.on('accept', this.eventConnectAccept)
    this._connect.on('disconnect', this.eventConnectDisconnect)
    this._connect.on('error', this.eventConnectError)
    this._connect.on('mute', this.eventConnectMute)
    this._connect.on('reconnected', this.eventConnectReconnected)
    this._connect.on('reconnecting', this.eventConnectReconnecting)
    this._connect.on('reject', this.eventConnectReject)
    this._connect.on('ringing', this.eventConnectRinging)
    this._connect.on('sample', this.eventConnectSample)
    this._connect.on('warning', this.eventConnectWarning)
  }

  eventConnectWarning = (warningName: string, warningData: string) => {
    if (!this._connect) return

    const params = {
      warning: warningName,
      details: warningData,
    }

    this.log('[Twilio] - eventConnectWarning')

    sentEventSentry({
      event: '[Twilio:Calling] Event ConnectWarning',
      name: 'calling',
      level: 'warning',
      tags: {
        data: JSON.stringify(params),
      },
      sampleRate: 0.2,
    })

    return CallApi.updateVoiceCallBySidQualityLog(this._connect.parameters.CallSid, params)
      .then((response: AxiosResponse) => {
        return response.data.success
      })
      .catch((e) => {
        return e.response
      })
  }

  // Twilio Voice SDK 2.12.4 emits 'cancel' with no arguments (call.js:267).
  // We resolve the active call from this._connect — accessing connect.parameters
  // here would crash because connect is undefined.
  eventConnectCancel = () => {
    const callSid = this._connect?.parameters.CallSid

    this._incomingCallCancelRequest.reset()
    this.updateConnect()
    this.stopRing(callSid)
    if (callSid) {
      this._sharedWorkerCallStore.notifyCallEnded(callSid)
    }
    this._sharedWorkerCallStore.setBusy(false)
    this.hideCall()
    this.log('[Twilio] - eventConnectCancel')
  }

  eventConnectAccept = async (connect: Call) => {
    this.stopRing(connect.parameters.CallSid)
    this._sharedWorkerCallStore.notifyCallAccepted(connect.parameters.CallSid)
    this._sharedWorkerCallStore.setBusy(true)

    this.updateConnect(connect)
    this.activeCallingStore.sendCallingEvent(true)
    this.activeCallingStore.sendConversationCallingEvent(true)
    this.log('[Twilio] - eventConnectAccept')

    if (this._device?.audio) {
      const selectedDevice = this.deviceSettingsStore.device.audioOutput
      if (selectedDevice?.deviceId) {
        try {
          if (
            this._device.audio.speakerDevices &&
            typeof this._device.audio.speakerDevices.set === 'function'
          ) {
            await this._device.audio.speakerDevices.set(selectedDevice.deviceId)
          }
        } catch (e) {
          logger.error(`[eventConnectAccept] Failed to set speaker device: ${e}`)
        }
      }
    }
  }

  eventConnectDisconnect = (connect: Call) => {
    this.stopRing(connect.parameters.CallSid)
    this._sharedWorkerCallStore.notifyCallEnded(connect.parameters.CallSid)
    this._sharedWorkerCallStore.setBusy(false)

    this.activeCallingStore.sendCallingEvent(false)
    this.activeCallingStore.sendConversationCallingEvent(false)
    this.updateConnect(connect)
    this.log('[Twilio] - eventConnectDisconnect')
    this.hideCall()
  }

  eventConnectError = (error: TwilioError.TwilioError) => {
    this.updateConnect()
    this.log('[Twilio] - eventConnectError')

    const twilioError = error.originalError?.twilioError
    const level1 = twilioError ? ' Level 1' : ''

    sentEventSentry({
      event: `[Twilio:Calling] Event ConnectError${level1}`,
      error: error,
      name: 'calling',
      level: 'error',
      sampleRate: 0.2,
    })

    if (twilioError) {
      sentEventSentry({
        event: '[Twilio:Calling] Event ConnectError Level 2',
        error: twilioError,
        name: 'calling',
        level: 'error',
        sampleRate: 0.2,
      })
    }
  }

  // Twilio Voice SDK 2.12.4 emits 'mute' as (isMuted: boolean, call: Call)
  // (call.js:1009) — the first argument is a boolean, not a string.
  eventConnectMute = (isMuted: boolean, connect: Call) => {
    this.updateConnect(connect)
    this.log('[Twilio] - eventConnectMute')

    sentEventSentry({
      event: '[Twilio:Calling] Event ConnectMute',
      name: 'calling',
      level: 'log',
      tags: {
        enableMute: isMuted,
      },
      sampleRate: 0.2,
    })
  }

  // Twilio Voice SDK 2.12.4 emits 'reconnected' with no arguments (call.js:394, 516).
  eventConnectReconnected = () => {
    this.updateConnect()
    this.log('[Twilio] - eventConnectReconnected')

    sentEventSentry({
      event: '[Twilio:Calling] Event Reconnected',
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })
  }

  // Twilio Voice SDK 2.12.4 emits 'reconnecting' with a TwilioError, not a generic Error
  // (call.js:377, 532). Using TwilioError.TwilioError preserves .code / .originalError.
  eventConnectReconnecting = (error: TwilioError.TwilioError) => {
    this.log('[Twilio] - eventConnectReconnecting')

    sentEventSentry({
      event: '[Twilio:Calling] Event Reconnecting',
      error: error,
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })
  }

  eventConnectReject = () => {
    const callSid = this._connect?.parameters.CallSid
    this.updateConnect()
    this.stopRing(callSid)
    if (callSid) {
      this._sharedWorkerCallStore.notifyCallEnded(callSid)
    }
    this._sharedWorkerCallStore.setBusy(false)
    this.log('[Twilio] - eventConnectReject')
  }

  eventConnectRinging = () => {
    this.updateConnect()
    this.log('[Twilio] - eventConnectRinging')
  }

  eventConnectSample = () => {
    this.log('[Twilio] - eventConnectSample')
  }

  log = (title: string) => {
    if (NODE_ENV !== 'production') {
      console.log(title, this._status)
    }
  }

  // other methods
  connectTwilio = async (conversationId: number, extraParams?: IConnectTwilioExtraParams) => {
    if (this._status !== Call.State.Closed) return console.warn('Connection already opened')

    this.setStartedCall(true)

    const conversation = await this._conversationStore.getById({
      id: conversationId,
    })

    if (!conversation) return

    const contactFrom = await this._inboxesStore.getById(conversation.inbox_id)
    const contactTo = await this._contactsStore.getById(conversation.contact_id)

    if (contactFrom?.type !== 'inbox') return
    if (!contactTo) return
    if (
      conversation?.is_aircall ||
      this._organizationStore.organization_call?.call_via_aircall?.is_active ||
      contactFrom?.call_via_aircall?.is_active
    ) {
      if (contactTo?.number) {
        writeTextToClipboard(contactTo.number).then(() => {
          window.open('https://phone.aircall.io/keyboard', '_blank')
        })

        this.setStartedCall(false)
      }

      return
    }

    runInAction(() => {
      this._contactTo = contactTo
      this._contactFrom = contactFrom
      this._inboxId = conversation.inbox_id
      this._conversationId = conversation.id
      this._signal = {
        key: nanoid(),
        name: 'outgoing',
      }
    })

    const isAllowed = await this.checkMicrophone()
    if (!isAllowed) {
      toastStore.add({
        type: 'error',
        title: 'Microphone access required',
        desc: 'To enable calling, please allow Salesmsg to access your microphone.',
        action: {
          link: LINK_ENABLE_MICROPHONE,
          text: 'Learn more',
        },
      })

      this.setStartedCall(false)

      return
    }

    return new Promise<void>(async (resolve) => {
      if (!this._device) return resolve()
      if (!contactTo?.number) return resolve()

      // Mark this tab busy so the worker doesn't land a parallel inbound here.
      // From this point on, every exit path must reset busy via one of the
      // Twilio connect-event handlers (accept/disconnect/cancel/reject) or
      // via the catch below.
      this._sharedWorkerCallStore.setBusy(true)

      try {
        const connect = await this._device.connect({
          params: {
            To: contactTo.number,
            ConversationId: String(conversation.id),
            ...(extraParams?.powerDialerId && { PowerDialerId: String(extraParams.powerDialerId) }),
          },
        })

        runInAction(() => {
          this._connect = connect
        })

        this.eventsConnect()

        const dispose = reaction(
          () => this._status,
          (status) => {
            if (status === Call.State.Closed) {
              dispose()
              this.setStartedCall(false)
              resolve()
            }
          }
        )
      } catch (e) {
        // device.connect() rejected — no Twilio events will fire, so we have
        // to clear busy ourselves; otherwise the worker keeps routing
        // inbound calls away from this tab forever.
        this._sharedWorkerCallStore.setBusy(false)
        this.setStartedCall(false)
        logger.error(e)
        resolve()
      }
    })
  }

  makeCall = async ({
    type,
    payload,
  }: {
    type: 'phoneNumber'
    payload: {
      phoneNumber: string
      inbox_id: number
      number_id?: number
      smart_option?: IParamsSendFromSmartOption
    }
  }) => {
    if (type === 'phoneNumber') {
      const { phoneNumber, number_id, inbox_id, smart_option } = payload
      const contact = await this._contactsStore.getContactByNumber(phoneNumber, true)
      if (contact) {
        const conversation = await this._conversationStore.createConversation({
          contact_id: contact.id,
          team_id: inbox_id,
          number_id,
          smart_option,
        })

        if (conversation instanceof Conversation) {
          this._conversationId = conversation.id

          return this.connectTwilio(conversation.id)
        }
      }
    }
  }

  disconnectTwilio = (isClientDisconnect = true) => {
    if (isClientDisconnect) {
      if (this._status === Call.State.Pending) {
        this._connect?.reject()
      }
    }

    this._device?.disconnectAll()
    return this.hideCall()
  }

  acceptTwilio = () => {
    this._connect?.accept()
    this.updateConnect()
  }

  sendDigitsTwilio = (value: string) => {
    this._connect?.sendDigits(value)
  }

  testOutputDevice = async () => {
    if (!this._device?.audio) return

    const selectedDevice = this.deviceSettingsStore.device.audioOutput
    if (selectedDevice?.deviceId) {
      try {
        await this._device.audio.ringtoneDevices.set(selectedDevice.deviceId)
      } catch (e) {
        logger.error(e)
      }
    }

    this._device.audio.ringtoneDevices.test()
  }

  stopStreamInputTrack = (stream?: MediaStream | null) => {
    if (!stream) return

    stream.getAudioTracks().forEach((track) => {
      track.stop()
    })
  }

  stopStreamInputDeviceTrack = () => {
    this.stopStreamInputTrack(this._device?.audio?.inputStream)
  }

  addStream = (stream: MediaStream) => {
    this._streams.push(stream)
  }

  stopStreams = () => {
    this._streams.forEach((stream) => {
      this.stopStreamInputTrack(stream)
    })

    const stream = this._connect?.getLocalStream()

    this.stopStreamInputTrack(stream)

    this._streams = []
  }

  handleMute = (status: boolean) => {
    if (!this._connect) return

    this._connect?.mute(status)
  }

  /**
   * Start the inbound ringtone on this tab.
   * Called by SharedWorkerCallStore when the worker has elected this tab
   * as the ringing tab for the given callSid.
   *
   * The check against the current Twilio call's SID guards against late
   * messages from the worker for a call that has already been replaced.
   */
  startRing = (callSid?: string) => {
    if (!this._device) return
    if (callSid && this._connect && this._connect.parameters.CallSid !== callSid) return

    if (this._isRingEverywhere && this._device.audio) {
      this.startRingEverywhere()
      return
    }

    // TODO: https://github.com/twilio/twilio-voice.js/issues/222
    this._device._soundcache.get(Device.SoundName.Incoming)?.play()
  }

  /**
   * Stop the inbound ringtone on this tab.
   * Called by the worker on accept/end/handoff and locally on the same
   * connect events as a safety net.
   */
  stopRing = (callSid?: string) => {
    if (!this._device) return
    if (callSid && this._connect && this._connect.parameters.CallSid !== callSid) return

    this.stopRingEverywhere()
    // TODO: https://github.com/twilio/twilio-voice.js/issues/222
    const sound = this._device._soundcache.get(Device.SoundName.Incoming)
    if (sound?.isPlaying) sound.stop()
  }

  handleMuteIncomingNotification = async (status: boolean) => {
    if (!this._device) return

    try {
      if (status) {
        this.stopRingEverywhere()
        // TODO: https://github.com/twilio/twilio-voice.js/issues/222
        this._device?._soundcache.get(Device.SoundName.Incoming).stop()
        this._device.audio?.incoming(status)
        return
      }

      if (this._isRingEverywhere && this._device.audio) {
        this.startRingEverywhere()
      } else {
        if (this._device.audio && !status) {
          const selectedDevice = this.deviceSettingsStore.device.audioOutput

          if (selectedDevice?.deviceId) {
            try {
              const availableDevices = Array.from(
                this._device.audio.availableOutputDevices.values()
              )
              const isDeviceAvailable = availableDevices.some(
                (device) => device.deviceId === selectedDevice.deviceId
              )

              if (!isDeviceAvailable) {
                const defaultDevice = availableDevices.find((d) => d.deviceId === 'default')
                if (defaultDevice) {
                  if (
                    this._device.audio.speakerDevices &&
                    typeof this._device.audio.speakerDevices.set === 'function'
                  ) {
                    await this._device.audio.speakerDevices.set(defaultDevice.deviceId)
                    await this._device.audio.ringtoneDevices.set(defaultDevice.deviceId)
                  }
                }
              } else {
                if (
                  this._device.audio.speakerDevices &&
                  typeof this._device.audio.speakerDevices.set === 'function'
                ) {
                  await this._device.audio.speakerDevices.set(selectedDevice.deviceId)
                  await this._device.audio.ringtoneDevices.set(selectedDevice.deviceId)
                }
              }
            } catch (e) {
              logger.error(`[handleMuteIncomingNotification] Failed to set output device: ${e}`)
            }
          }
        }

        this._device.audio?.incoming(status)
        this._device?._soundcache.get(Device.SoundName.Incoming)?.play()
      }
    } catch (e) {
      logger.error(e)
    }
  }

  startRingEverywhere = async () => {
    if (!this._isRingEverywhere) {
      if (this._device?.audio) {
        this._device.audio.incoming(false)
        this._device?._soundcache.get(Device.SoundName.Incoming).play()
      }
      return
    }

    if (!this._device?.audio) {
      this._device?.audio?.incoming(false)
      return
    }

    this.stopRingEverywhere()
    this._ringEverywhereStopped = false

    try {
      const deviceAudio = this._device.audio
      const outputDevices = Array.from(deviceAudio.availableOutputDevices.values())

      if (outputDevices.length === 0) {
        this._device.audio.incoming(false)
        return
      }

      const incomingSound = this._device?._soundcache.get(Device.SoundName.Incoming)
      if (!incomingSound) {
        this._device.audio.incoming(false)
        return
      }

      const sound: TwilioSound = incomingSound
      const soundAudio = sound._audio
      const soundSrc = sound._src
      const soundUrl = sound.url

      let audioSrc = soundAudio?.src || soundAudio?.currentSrc || soundSrc || soundUrl

      if (
        audioSrc &&
        (audioSrc.includes(window.location.hostname) || audioSrc.startsWith(window.location.origin))
      ) {
        audioSrc = soundUrl || soundSrc || undefined
      }

      if (!audioSrc && soundAudio) {
        try {
          await soundAudio.play()
          await new Promise((resolve) => setTimeout(resolve, 100))
          soundAudio.pause()
          soundAudio.currentTime = 0
          const newSrc = soundAudio.src || soundAudio.currentSrc
          if (
            newSrc &&
            !newSrc.includes(window.location.hostname) &&
            !newSrc.startsWith(window.location.origin)
          ) {
            audioSrc = newSrc
          }
        } catch (e) {
          logger.error(`Failed to trigger sound load: ${e}`)
        }

        if (!audioSrc) {
          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              const newSrc = soundAudio.src || soundAudio.currentSrc
              if (
                newSrc &&
                !newSrc.includes(window.location.hostname) &&
                !newSrc.startsWith(window.location.origin)
              ) {
                audioSrc = newSrc
                clearInterval(checkInterval)
                resolve()
              }
            }, 100)

            setTimeout(() => {
              clearInterval(checkInterval)
              resolve()
            }, 3000)
          })
        }
      }

      if (
        audioSrc &&
        (audioSrc.includes(window.location.hostname) || audioSrc.startsWith(window.location.origin))
      ) {
        audioSrc = soundUrl || audioSrc
      }

      if (!audioSrc || audioSrc === window.location.href) {
        this._device.audio.incoming(false)
        return
      }

      const setupPromises = outputDevices.map(async (outputDevice) => {
        try {
          const audio = new Audio()
          audio.loop = true
          audio.volume = 1.0
          audio.preload = 'auto'

          const audioWithSinkId: HTMLAudioElementWithSinkId = audio

          if (audioWithSinkId.setSinkId && typeof audioWithSinkId.setSinkId === 'function') {
            try {
              await audioWithSinkId.setSinkId(outputDevice.deviceId)

              if (typeof audioSrc === 'string' && audioSrc && audioSrc !== window.location.href) {
                audio.src = audioSrc
              } else {
                logger.error('[startRingEverywhere] audioSrc is not a valid string')
                return null
              }

              const loadTimeout = 8000

              await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => {
                  logger.error(`[${outputDevice.label}] Load timeout after ${loadTimeout}ms`)
                  reject(new Error('Load timeout'))
                }, loadTimeout)

                audio.addEventListener(
                  'canplaythrough',
                  () => {
                    clearTimeout(timeout)
                    resolve()
                  },
                  { once: true }
                )

                audio.addEventListener(
                  'error',
                  (e) => {
                    clearTimeout(timeout)
                    logger.error(`[${outputDevice.label}] Audio error event: ${e}`)
                    reject(e)
                  },
                  { once: true }
                )

                audio.addEventListener('loadeddata', () => {}, { once: true })
                audio.load()
              })

              return { audio, device: outputDevice }
            } catch (e) {
              logger.error(
                `[${outputDevice.label}] Failed to setup audio: ${
                  e instanceof Error ? e.message : e
                }`
              )
              return null
            }
          } else {
            logger.warn(`[${outputDevice.label}] setSinkId not supported by browser`)
            return null
          }
        } catch (e) {
          logger.error(`[${outputDevice.label}] Error setting up device: ${e}`)
          return null
        }
      })

      const audioElementsWithDevices = (await Promise.all(setupPromises)).filter(
        (item): item is { audio: HTMLAudioElement; device: MediaDeviceInfo } => item !== null
      )
      if (this._ringEverywhereStopped) {
        audioElementsWithDevices.forEach(({ audio }) => {
          try {
            audio.pause()
            audio.src = ''
            audio.load()
          } catch (e) {
            logger.error(`[startRingEverywhere] Failed to cleanup audio: ${e}`)
          }
        })
        return
      }

      if (audioElementsWithDevices.length > 0) {
        this._ringEverywhereAudioElements = audioElementsWithDevices.map((item) => item.audio)

        if (this._ringEverywhereStopped) {
          this.stopRingEverywhere()
          return
        }

        const playPromises = audioElementsWithDevices.map(async ({ audio, device }) => {
          try {
            await audio.play()
          } catch (e) {
            logger.error(
              `[${device.label}] Failed to play audio: ${e instanceof Error ? e.message : e}`
            )
          }
        })

        await Promise.allSettled(playPromises)
      } else {
        if (this._device?.audio) {
          this._device.audio.incoming(false)
        }
      }
    } catch (e) {
      logger.error(`Error in startRingEverywhere: ${e instanceof Error ? e.message : e}`)
      if (this._device?.audio) {
        this._device.audio.incoming(false)
      }
    }
  }

  stopRingEverywhere = () => {
    this._ringEverywhereStopped = true

    if (this._ringEverywhereAudioElements.length > 0) {
      this._ringEverywhereAudioElements.forEach((audio, index) => {
        try {
          audio.pause()
          audio.currentTime = 0
          audio.src = ''
          audio.load()
        } catch (e) {
          logger.error(`[stopRingEverywhere] Failed to stop audio element ${index + 1}: ${e}`)
        }
      })
      this._ringEverywhereAudioElements = []
    }
  }

  handleChangeRecord = async () => {
    try {
      runInAction(() => {
        this._isRecord = !this._isRecord
        this._isRecordLoading = true
      })

      await UsersApi.updateUsersToggleRecordAutomatically({
        isRecordAutomatically: this._isRecord,
      })

      runInAction(() => {
        this._usersStore.isRecordAutomatically = this._isRecord
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isRecordLoading = false
      })
    }
  }

  handleChangeRingEverywhere = async () => {
    try {
      runInAction(() => {
        this._isRingEverywhere = !this._isRingEverywhere
        this._isRingEverywhereLoading = true
      })

      await UsersApi.updateUsersToggleRingEverywhere({
        isRingEverywhere: this._isRingEverywhere,
      })

      runInAction(() => {
        this._usersStore.isRingEverywhere = this._isRingEverywhere
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isRingEverywhereLoading = false
      })
    }
  }

  setStartedCall = (value: boolean) => {
    this._started = value
  }

  reactionSelectedAudioOutput = () => {
    this._disposeSelectedAudioOutput?.()
    this._disposeSelectedAudioOutput = reaction(
      () => this.deviceSettingsStore.device.audioOutput,
      (value) => {
        if (!value) return
        if (this._status !== Call.State.Closed) return

        this.setOutputDevice(value.deviceId)
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionSelectedAudioInput = () => {
    this._disposeSelectedAudioInput?.()
    this._disposeSelectedAudioInput = reaction(
      () => this.deviceSettingsStore.device.audioInput,
      (value) => {
        if (!value) return
        if (this._status !== Call.State.Closed) return

        this.deviceSettingsStore.removeStream()
        this.setInputDevice(value.deviceId)
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionStatus = () => {
    this._disposeStatus?.()
    this._disposeStatus = reaction(
      () => this._status,
      async (value) => {
        if (value === Call.State.Open) {
          this.setStartedCall(true)
        }
        if (value === Call.State.Closed) {
          this.setStartedCall(false)
        }
      }
    )
  }

  reactionUserSettings = () => {
    this._disposeUserSettings?.()
    this._disposeUserSettings = reaction(
      () => ({
        isRingEverywhere: this._usersStore.isRingEverywhere,
        isRecordAutomatically: this._usersStore.isRecordAutomatically,
      }),
      (value) => {
        runInAction(() => {
          this._isRingEverywhere = value.isRingEverywhere
          this._isRecord = value.isRecordAutomatically
        })
      }
    )
  }

  get isIncoming() {
    return this._direction === Call.CallDirection.Incoming
  }

  get isOutgoing() {
    return this._direction === Call.CallDirection.Outgoing
  }

  get isStatusClosed() {
    return this._status === Call.State.Closed
  }

  get isAcceptVoice() {
    if (this.isIncoming) {
      return this._status === Call.State.Open
    }

    return this.isOutgoing
  }

  get isShowCall() {
    if (this._inboxesStore.currentInbox?.type !== 'inbox') return true

    return this._inboxesStore.currentInbox?.isNumberOutboundCalls
  }

  get isStatusClose() {
    return this._status === Call.State.Closed
  }

  get isBusy() {
    return this._started || this._status !== Call.State.Closed
  }

  get device() {
    return this._device
  }

  get connect() {
    return this._connect
  }

  get loading() {
    return this._loading
  }

  get disabled() {
    return this._disabled
  }

  get voicemailDropCompleted() {
    return this._voicemailDropCompleted
  }

  get contactTo() {
    return this._contactTo
  }

  get contactFrom() {
    return this._contactFrom
  }

  get status() {
    return this._status
  }

  get isRecord() {
    return this._isRecord
  }

  get isRingEverywhere() {
    return this._isRingEverywhere
  }

  get isRingEverywhereLoading() {
    return this._isRingEverywhereLoading
  }

  get isRecordLoading() {
    return this._isRecordLoading
  }

  get signal() {
    return this._signal
  }

  get startedCall() {
    return this._started
  }

  get conversationId() {
    return this._conversationId
  }

  get inboxId() {
    return this._inboxId
  }

  isInboxOnCall = (inboxId: number): boolean => {
    return this.activeCallingStore.isInboxOnCall(inboxId)
  }
}
